import { CircularProgress, Divider, List } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import * as React from "react";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { DatasetRecord } from "@/shared/domain/datasets";
import { useInfiniteSuspenseDatasetsQuery } from "@/shared/domain/datasets/hooks";
import { PaginatedResponse } from "@/shared/http";
import { Condition } from "@/types";

import styles from "./DatasetList.module.css";
import { DatasetListItem } from "./DatasetListItem";

interface DatasetListProps {
  activeDatasetId: string | null;
  orgId: string;
  onDatasetSelect: (datasetId: string) => void;
  getSelectedCountForDataset: (datasetId: string) => number;
  activeDatasetIds?: string[];
}

/**
 * DatasetList component that displays a list of datasets with pagination.
 * Active datasets (from current workspace) are displayed at the top, followed by other datasets.
 */
export function DatasetList(props: DatasetListProps) {
  const {
    activeDatasetId,
    orgId,
    onDatasetSelect,
    getSelectedCountForDataset,
    activeDatasetIds,
  } = props;

  // Fetch active datasets separately
  const activeDatasetsQuery = useActiveDatasets(activeDatasetIds);

  // Fetch all other datasets, excluding active ones to prevent duplication
  const datasetsQuery = useInfiniteSuspenseDatasetsQuery({
    orgId,
    excludeDatasetIds: activeDatasetIds,
    limit: 50,
  });

  // Auto-select first dataset when datasets load
  // Prefer active datasets, fall back to first dataset in list
  React.useEffect(
    function autoSelectFirstDataset() {
      if (activeDatasetId) {
        return; // Already have an active selection
      }

      const hasActiveData = activeDatasetIds && activeDatasetIds.length > 0;
      const hasOtherData = datasetsQuery.data && datasetsQuery.data.length > 0;

      if (hasActiveData) {
        // Prefer first active dataset
        onDatasetSelect(activeDatasetIds[0]);
      } else if (hasOtherData) {
        // Fall back to first dataset in list
        onDatasetSelect(datasetsQuery.data[0].dataset_id);
      }
    },
    [activeDatasetId, activeDatasetIds, datasetsQuery.data, onDatasetSelect],
  );

  const hasActiveDatasets = activeDatasetIds && activeDatasetIds.length > 0;
  const hasOtherDatasets = datasetsQuery.data && datasetsQuery.data.length > 0;

  if (!hasActiveDatasets && !hasOtherDatasets) {
    return (
      <div className={styles.emptyContainer}>
        <RobotoTypography color="text.secondary" variant="body2">
          No datasets found
        </RobotoTypography>
      </div>
    );
  }

  return (
    <div className={styles.listContainer}>
      <List dense disablePadding>
        {/* Active datasets section */}
        {hasActiveDatasets && (
          <>
            {activeDatasetsQuery.data.map((dataset: DatasetRecord) => (
              <DatasetListItem
                key={dataset.dataset_id}
                dataset={dataset}
                orgId={orgId}
                isActive={activeDatasetId === dataset.dataset_id}
                onSelect={onDatasetSelect}
                getSelectedCountForDataset={getSelectedCountForDataset}
              />
            ))}
            {hasOtherDatasets && <Divider sx={{ my: 1 }} />}
          </>
        )}

        {/* Other datasets */}
        {datasetsQuery.data.map((dataset: DatasetRecord) => (
          <DatasetListItem
            key={dataset.dataset_id}
            dataset={dataset}
            orgId={orgId}
            isActive={activeDatasetId === dataset.dataset_id}
            onSelect={onDatasetSelect}
            getSelectedCountForDataset={getSelectedCountForDataset}
          />
        ))}
      </List>

      {datasetsQuery.hasNextPage && (
        <button
          aria-labelledby="load-more-datasets"
          className={styles.loadMoreContainer}
          disabled={datasetsQuery.isFetchingNextPage}
          onClick={() => {
            void datasetsQuery.fetchNextPage();
          }}
        >
          {datasetsQuery.isFetchingNextPage ? (
            <CircularProgress size={16} />
          ) : (
            <span id="load-more-datasets">Load more</span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * Fetches multiple datasets by their IDs using a single bulk query.
 * Uses Suspense for loading states.
 */
function useActiveDatasets(datasetIds: string[] | undefined) {
  const { datasetService } = useDomainServices();
  const { currentOrganization } = useAuth();

  return useSuspenseQuery({
    queryKey: ["activeDatasets", datasetIds],
    queryFn: async ({ signal }): Promise<PaginatedResponse<DatasetRecord>> => {
      if (!datasetIds || datasetIds.length === 0) {
        return { items: [], next_token: undefined };
      }

      // Create OR condition group for all dataset IDs
      const condition: Condition = {
        operator: "OR",
        conditions: datasetIds.map((id) => ({
          field: "dataset_id",
          comparator: "EQUALS",
          value: id,
        })),
      };

      return datasetService.queryDatasets(
        {
          condition,
          limit: datasetIds.length,
        },
        {
          orgId: currentOrganization?.org_id,
          abortSignal: signal,
        },
      );
    },
    select: (data) => data.items,
  });
}
